
import { Elements } from "@stripe/react-stripe-js";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { PaymentForm } from "@/components/settings/PaymentForm";

interface PaymentDialogProps {
  showPaymentDialog: boolean;
  setShowPaymentDialog: (show: boolean) => void;
  selectedPlan: string | null;
  stripePromise: Promise<any> | null;
  onSuccess: (paymentMethodId: string) => void;
  onError: (error: Error) => void;
  loading: boolean;
  paymentDescription?: string;
}

export const PaymentDialog = ({
  showPaymentDialog,
  setShowPaymentDialog,
  selectedPlan,
  stripePromise,
  onSuccess,
  onError,
  loading,
  paymentDescription
}: PaymentDialogProps) => {
  // Map price IDs to human-readable plan names
  const getPlanNameFromPriceId = (priceId: string | null): string => {
    if (!priceId) return "Selected";
    
    const planMap: Record<string, string> = {
      "price_1QvOaiG3RRUf49cQgmkP8MtT": "Monthly",
      "price_1QvObYG3RRUf49cQp6XPRkNN": "Yearly",
      "price_1QvOcGG3RRUf49cQlU0O22em": "Lifetime"
    };
    
    return planMap[priceId] || "Selected";
  };

  const planName = getPlanNameFromPriceId(selectedPlan);

  // Get appropriate payment description based on plan
  const getPaymentDescriptionByPlan = () => {
    if (!selectedPlan) return "You'll be charged immediately for this subscription.";

    switch(selectedPlan) {
      case "price_1QvOaiG3RRUf49cQgmkP8MtT":
        return "You'll be charged $4.99/month for this subscription.";
      case "price_1QvObYG3RRUf49cQp6XPRkNN":
        return "You'll be charged $29.99/year for this subscription.";
      case "price_1QvOcGG3RRUf49cQlU0O22em":
        return "You'll be charged a one-time payment of $199.99 for lifetime access.";
      default:
        return paymentDescription || "You'll be charged immediately for this subscription.";
    }
  };

  return (
    <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto px-4 sm:px-6 w-[calc(100%-2rem)] mx-auto">
        <DialogHeader>
          <DialogTitle>Subscribe to {planName} Plan</DialogTitle>
        </DialogHeader>

        <Alert className="mb-4">
          <AlertCircle className="h-4 w-4 mr-2" />
          <AlertDescription>
            {getPaymentDescriptionByPlan()}
          </AlertDescription>
        </Alert>

        {stripePromise && (
          <Elements stripe={stripePromise}>
            <PaymentForm
              onSuccess={onSuccess}
              onError={onError}
              planType={selectedPlan || ''}
              loading={loading}
            />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
};
